import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Flame, Menu, Sun, X } from 'lucide-react';
import PortableHeaterIcon from './icons/PortableHeaterIcon';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';
import { newsLocale } from '../src/news/api';
import { newsUi } from '../src/news/ui';

import Link from './LocaleLink';
interface NavbarProps {
  onOpenQuote: () => void;
}

/** The three catalogue categories, collapsed into a single "Products" entry. */
const CATEGORY_ITEMS = [
  {
    href: '/infrared-heaters',
    labelKey: 'products.categories.infraredHeater',
    descKey: 'products.navDesc.infrared',
    icon: Sun,
    iconClassName: 'text-orange-700',
  },
  {
    href: '/portable-heaters',
    labelKey: 'products.categories.portableHeater',
    descKey: 'products.navDesc.portable',
    icon: PortableHeaterIcon,
    iconClassName: 'text-slate-700',
  },
  {
    href: '/gas-heaters',
    labelKey: 'products.categories.gasHeater',
    descKey: 'products.navDesc.gas',
    icon: Flame,
    iconClassName: 'text-amber-800',
  },
];

const PRODUCT_PATHS = ['/products', '/product', ...CATEGORY_ITEMS.map((c) => c.href)];

const Navbar: React.FC<NavbarProps> = ({ onOpenQuote }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isMobileGroupOpen, setIsMobileGroupOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const { t, i18n } = useTranslation();
  const closeTimer = useRef<number | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close both menus whenever the route changes.
  useEffect(() => {
    setIsOpen(false);
    setIsProductsOpen(false);
  }, [location.pathname]);

  const openProductsMenu = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setIsProductsOpen(true);
  };

  const scheduleProductsClose = () => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setIsProductsOpen(false), 120);
  };

  // Determine text color based on scroll state and current page
  const getTextColor = () => {
    if (isScrolled || !isHome) return 'text-gray-700';
    return 'text-gray-200';
  };

  const getNavBg = () => {
    if (isScrolled || !isHome) return 'bg-white shadow-md py-3';
    return 'bg-transparent py-5';
  };

  const isProductsActive = PRODUCT_PATHS.some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
  );

  const isActiveHref = (href: string) => location.pathname === href;

  const navItems = [
    { href: '/technology', label: t('nav.technology') },
    { href: '/installations', label: t('installationsPage.nav') },
    { href: '/news', label: newsUi[newsLocale(i18n.resolvedLanguage || i18n.language)].nav },
    { href: '/about', label: t('nav.about') },
    { href: '/contact', label: t('nav.contact') },
  ];

  return (
    <nav
      data-home={isHome}
      data-scrolled={isScrolled}
      className={`site-navbar fixed w-full z-40 transition-all duration-300 ${getNavBg()}`}
    >
      <div className="site-chrome-container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="site-navbar__row flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <img 
              src="/brand.png" 
              alt={i18n.language?.startsWith('ru') ? 'JH Heatsup — производитель инфракрасных обогревателей' : 'JH Heatsup - Infrared Heater Manufacturer'}
              width="160"
              height="40"
              className="h-10 w-auto object-contain" 
            />
          </Link>

          {/* Desktop Menu */}
          <div className="site-navbar__desktop hidden lg:flex gap-5 xl:gap-8 items-center">
            {/* Products with hover dropdown */}
            <div
              className="relative"
              onMouseEnter={openProductsMenu}
              onMouseLeave={scheduleProductsClose}
            >
              <Link
                to="/products"
                onFocus={openProductsMenu}
                onBlur={() => setIsProductsOpen(false)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setIsProductsOpen(false);
                }}
                aria-haspopup="true"
                aria-expanded={isProductsOpen}
                className={`site-nav-link inline-flex items-center gap-1 h-9 text-sm font-medium leading-none hover:text-orange-500 transition-colors ${
                  isProductsActive ? 'text-orange-500 font-bold' : getTextColor()
                }`}
              >
                {t('nav.products')}
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform duration-200 ${
                    isProductsOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </Link>

                <div
                  className={`absolute top-full start-0 pt-2 transition-all duration-200 ${
                  isProductsOpen
                    ? 'opacity-100 translate-y-0 visible'
                    : 'opacity-0 -translate-y-1 invisible'
                }`}
              >
                <div className="site-navbar__catalogue w-72 rounded-2xl border border-gray-100 bg-white p-2 shadow-xl shadow-gray-900/10">
                  {CATEGORY_ITEMS.map((item) => {
                    const Icon = item.icon;
                    const active =
                      location.pathname === item.href ||
                      location.pathname.startsWith(`${item.href}/`);
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        className={`flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors ${
                          active ? 'bg-orange-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <Icon
                          className={`h-8 w-8 shrink-0 ${item.iconClassName}`}
                          strokeWidth={1.5}
                          aria-hidden="true"
                        />
                        <span className="flex flex-col leading-tight">
                          <span
                            className={`text-sm font-semibold ${
                              active ? 'text-orange-600' : 'text-gray-900'
                            }`}
                          >
                            {t(item.labelKey)}
                          </span>
                          <span className="mt-0.5 text-xs text-gray-500">
                            {t(item.descKey)}
                          </span>
                        </span>
                      </Link>
                    );
                  })}
                  <div className="mt-1 border-t border-gray-100 pt-1">
                    <Link
                      to="/products"
                      className="block rounded-xl px-3 py-2 text-center text-xs font-semibold uppercase tracking-wider text-orange-600 hover:bg-orange-50"
                    >
                      {t('products.viewAllProducts')}
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`site-nav-link inline-flex items-center h-9 text-sm font-medium leading-none hover:text-orange-500 transition-colors ${
                  isActiveHref(item.href)
                    ? 'text-orange-500 font-bold'
                    : getTextColor()
                }`}
              >
                {item.label}
              </Link>
            ))}
            <div className={`site-nav-language ${isScrolled || !isHome ? '' : 'text-white'}`}>
              <LanguageSwitcher />
            </div>
            <button
              onClick={onOpenQuote}
              className="site-navbar__quote bg-orange-600 hover:bg-orange-700 text-white px-5 py-2.5 rounded-full font-medium transition-all transform hover:scale-105 shadow-lg shadow-orange-600/30"
            >
              {t('quote.getQuote')}
            </button>
          </div>

          {/* Mobile Menu Button */}
          <div className="site-navbar__mobile-controls lg:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              aria-label={t(isOpen ? 'merchandising.closeMenu' : 'merchandising.openMenu')}
              aria-expanded={isOpen}
              aria-controls="site-mobile-navigation"
              className={`site-nav-toggle p-2 rounded-md focus:outline-none ${
                isScrolled || !isHome ? 'text-gray-600' : 'text-white'
              }`}
            >
              {isOpen ? <X className="h-6 w-6 text-gray-800" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      <div
        id="site-mobile-navigation"
        inert={!isOpen}
        className={`site-nav-mobile-menu lg:hidden fixed inset-x-0 top-[60px] bg-white shadow-xl transition-all duration-300 ease-in-out transform ${
          isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-5 pointer-events-none'
        }`}
      >
        <div className="site-navbar__mobile-content px-4 pt-2 pb-6 space-y-1">
          {/* Products group with expand/collapse */}
          <div className="rounded-md">
            <div className="flex items-center">
              <Link
                to="/products"
                className={`flex-1 block px-3 py-3 text-base font-medium ${
                  isProductsActive ? 'text-orange-600' : 'text-gray-700'
                }`}
                onClick={() => setIsOpen(false)}
              >
                {t('nav.products')}
              </Link>
              <button
                onClick={() => setIsMobileGroupOpen((v) => !v)}
                aria-expanded={isMobileGroupOpen}
                aria-label={t('nav.products')}
                className="p-3 text-gray-500"
              >
                <ChevronDown
                  className={`h-5 w-5 transition-transform duration-200 ${
                    isMobileGroupOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
            </div>
            <div
              className={`overflow-hidden transition-all duration-200 ${
                isMobileGroupOpen ? 'max-h-96' : 'max-h-0'
              }`}
            >
              <div className="ms-3 space-y-1 border-s border-gray-100 ps-3 pb-1">
                {CATEGORY_ITEMS.map(({ icon: Icon, ...item }) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium hover:bg-gray-50 ${
                      location.pathname === item.href
                        ? 'text-orange-600 bg-gray-50'
                        : 'text-gray-600'
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    <Icon
                      className={`h-5 w-5 shrink-0 ${item.iconClassName}`}
                      strokeWidth={1.5}
                      aria-hidden="true"
                    />
                    {t(item.labelKey)}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={`block px-3 py-3 rounded-md text-base font-medium hover:bg-gray-50 ${
                isActiveHref(item.href)
                  ? 'text-orange-600 bg-gray-50'
                  : 'text-gray-700'
              }`}
              onClick={() => setIsOpen(false)}
            >
              {item.label}
            </Link>
          ))}
          <button
            onClick={() => {
              setIsOpen(false);
              onOpenQuote();
            }}
            className="block w-full text-center mt-4 bg-orange-600 text-white px-3 py-3 rounded-md font-medium"
          >
            {t('quote.getQuote')}
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
